export type Language = 'pt' | 'en' | 'es';

export interface ResumeItem {
  id: string;
  title: string;
  company: string;
  description: string[];
  location: string;
  startDate: string;
  endDate: string;
  current: boolean;
  image: string;
}

export const languageOptions: { code: Language; label: string }[] = [
  { code: 'pt', label: 'PT' },
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
];

export const translations = {
  pt: {
    nav: {
      home: 'Home',
      about: 'Sobre',
      projects: 'Projetos',
      education: 'Educação',
      resume: 'Experiência',
      contact: 'Contato',
      blog: 'Blog',
      language: 'Idioma',
    },
    home: {
      eyebrow: 'Backend Developer',
      title: 'Construo soluções backend escaláveis para produtos digitais modernos.',
      description:
        'Olá, sou Átila Rocha, desenvolvedor especializado em Node.js, NestJS, TypeScript e arquitetura de APIs. Ajudo empresas a transformar ideias em sistemas robustos, seguros e prontos para crescer.',
      primaryAction: 'Fale comigo',
      secondaryAction: 'Ver experiências',
      backToTop: 'Subir ao topo',
    },
    about: {
      title: 'Sobre o Desenvolvedor',
      readMore: 'Ler Mais',
      readLess: 'Mostrar Menos',
      imageAltOne: 'Átila Rocha em momento profissional',
      imageAltTwo: 'Átila Rocha em perfil profissional',
      paragraphs: [
        'Olá, meu nome é Átila, e minha jornada no mundo da tecnologia é marcada por curiosidade, determinação e um desejo constante de aprender. Tudo começou durante minha graduação em Oceanografia, quando tive meu primeiro contato com programação ao analisar dados complexos com Matlab e Linux.',
        'Concluí Oceanografia e atualmente curso Análise e Desenvolvimento de Sistemas. Minha motivação vem de resolver problemas reais com eficiência, clareza técnica e soluções que possam evoluir junto com o produto.',
        'Hoje atuo principalmente com backend, APIs, bancos de dados, dashboards e sistemas web. Gosto de transformar regras de negócio em aplicações estáveis, bem estruturadas e fáceis de manter.',
        'Sou autodidata, estudo continuamente e busco aplicar boas práticas em arquitetura, versionamento, conteinerização e colaboração em equipe. Minha trajetória combina análise de dados, desenvolvimento web e experiência com projetos de impacto real.',
        'Uma das entregas de que mais me orgulho foi o desenvolvimento de uma aplicação de geolocalização para identificar bioativos na UFMA, conectando minha base em Oceanografia com desenvolvimento de software.',
        'Valorizo empatia, trabalho em equipe e excelência técnica. Acredito que boas soluções nascem quando entendemos o contexto das pessoas, do negócio e da tecnologia.',
      ],
    },
    projects: {
      title: 'Projetos',
      description: 'Principais aplicações em desenvolvimento e concluídas.',
      github: 'GitHub',
      deploy: 'Deploy',
      inProgressAlt: 'Projeto em desenvolvimento',
      items: [
        {
          name: 'Sistema de Votação',
          summary:
            'Sistema web para gerenciamento e realização de votações online, usando tecnologias modernas com foco em segurança e eficiência.',
          technologies: ['PHP', 'Laravel', 'Blade', 'JavaScript'],
          githubLink: 'https://github.com/AtilaRocha/voting-system',
          liveLink: '',
          image: 'projeto1.png',
          status: 'finished',
        },
        {
          name: 'Portfólio',
          summary: 'Projeto criado para apresentar minha carreira, experiências, projetos e trajetória na programação.',
          technologies: ['React', 'JavaScript', 'Typescript', 'HTML', 'CSS'],
          githubLink: '',
          liveLink: 'https://atilarocha.netlify.app/',
          image: 'projeto3.png',
          status: 'finished',
        },
        {
          name: 'Formulário - Trabalhe Conosco',
          summary:
            'Aplicação web para gerenciamento de formulários Trabalhe Conosco, com backend em PHP/Laravel, frontend em HTML, CSS e JavaScript, banco MySQL e ambiente Docker.',
          technologies: ['PHP', 'Laravel', 'Docker', 'MySQL', 'JavaScript'],
          githubLink: 'https://github.com/AtilaRocha/mxmz-tech',
          liveLink: '',
          image: 'projeto4.png',
          status: 'finished',
        },
        {
          name: 'HydroDataViewer',
          summary:
            'Aplicação em Django que consome a API da Agência Nacional de Águas para exibir dados hidrometeorológicos como níveis de água, precipitação e temperatura.',
          technologies: ['Python', 'Django'],
          githubLink: 'https://github.com/AtilaRocha/HydroDataViewer',
          liveLink: '',
          image: 'projeto2.png',
          status: 'unfinished',
        },
      ],
    },
    education: {
      title: 'Educação',
      description: 'Resumo das formações.',
      items: [
        {
          title: 'Análise e Desenvolvimento de Sistemas',
          institution: 'Ampli',
          location: '',
          startDate: 'Mar/2023',
          endDate: 'Atual',
          description:
            'Competências: levantamento de requisitos, desenvolvimento de software, programação orientada a objetos, Java, Python e MySQL.',
        },
        {
          title: 'Oceanografia',
          institution: 'UFMA - Universidade Federal do Maranhão',
          location: 'São Luís',
          startDate: 'Mar/2018',
          endDate: 'Jul/2024',
          description:
            'Base científica em análise de dados ambientais, comunicação, geografia, pesquisa aplicada e programação.',
        },
      ],
    },
    resume: {
      title: 'Experiência Profissional',
      description: 'Histórico de trabalho e experiências anteriores.',
      items: [
        {
          id: '10',
          title: 'Backend Developer',
          company: 'Infobase IT',
          description: [
            'Atuação em soluções de e-commerce, integrando APIs, microserviços e fluxos críticos como pedidos, pagamentos e estoque.',
            'Desenvolvimento com Node.js, NestJS e TypeScript, aplicando arquitetura limpa, código maintível e boas práticas para sistemas escaláveis.',
            'Deploy e sustentação com Docker, CI/CD, Git, Bitbucket, Jenkins e cloud AWS/Oracle em colaboração com time distribuído pela LATAM.',
          ],
          location: 'Remoto',
          startDate: 'Ago/2025',
          endDate: 'Atual',
          current: true,
          image: 'infobase_it_logo.jpg',
        },
        {
          id: '9',
          title: 'Desenvolvedor Freelancer',
          company: 'Freelance | Self-Employed',
          description: [
            'Desenvolvimento de aplicações web com Node.js, Next.js, Java, Spring Boot e bancos de dados relacionais.',
            'Implementação de projetos para clientes diversos, incluindo WebGIS, dashboards interativos e interfaces responsivas.',
            'Entrega de soluções completas, do levantamento técnico ao deploy e acompanhamento das funcionalidades.',
          ],
          location: 'Remoto',
          startDate: 'Jan/2024',
          endDate: 'Set/2025',
          current: false,
          image: 'default',
        },
        {
          id: '8',
          title: 'Desenvolvedor de Software',
          company: 'OneBit',
          description: [
            'Desenvolvimento e manutenção de aplicações, criando soluções robustas para necessidades de clientes.',
            'Implementação de interfaces dinâmicas e responsivas para uma experiência de usuário fluida.',
            'Configuração de ambientes conteinerizados, integrações com bancos de dados e apoio na construção de soluções escaláveis.',
          ],
          location: 'São Luís, Maranhão, Brasil · Híbrido',
          startDate: 'Out/2024',
          endDate: 'Mai/2025',
          current: false,
          image: 'onebit_logo.jpg',
        },
        {
          id: '7',
          title: 'Desenvolvedor de Software',
          company: 'Núcleo Baites - UFMA',
          description: [
            'Desenvolvimento do setup inicial de aplicações, criando bases sólidas para colaboração em equipe.',
            'Planejamento e prototipação de componentes de UI/UX para otimizar a experiência do usuário.',
            'Criação de sistemas para análise e processamento de imagens, dados geoespaciais e recursos sensíveis com controle de acesso.',
          ],
          location: 'São Luís, Maranhão, Brasil',
          startDate: 'Abr/2024',
          endDate: 'Mai/2025',
          current: false,
          image: 'logoBaites.png',
        },
        {
          id: '6',
          title: 'Desenvolvedor de Software',
          company: 'Maximize',
          description: [
            'Desenvolvimento de APIs e sistemas interativos para atender demandas de diferentes áreas.',
            'Participação na criação de interfaces responsivas e intuitivas, melhorando usabilidade e experiência geral.',
            'Gerenciamento de ambientes de desenvolvimento e produção com foco em disponibilidade, confiabilidade e escalabilidade.',
          ],
          location: 'São Luís, Maranhão, Brasil · Híbrido',
          startDate: 'Set/2024',
          endDate: 'Out/2024',
          current: false,
          image: 'maximize_logo.jpeg',
        },
        {
          id: '5',
          title: 'Analista de Dados',
          company: 'SEMA - Secretaria Estadual do Meio Ambiente',
          description: [
            'Análise e monitoramento ambiental do Estado do Maranhão.',
            'Elaboração de relatórios ambientais com Python e Excel para análise, automação e apresentação de dados.',
          ],
          location: 'São Luís, Maranhão, Brasil · Presencial',
          startDate: 'Jan/2023',
          endDate: 'Ago/2023',
          current: false,
          image: 'logoSema.jpg',
        },
        {
          id: '4',
          title: 'Estagiário',
          company: 'SEMA - Secretaria Estadual do Meio Ambiente',
          description: [
            'Apoio em rotinas administrativas e técnicas ligadas a dados ambientais.',
            'Uso de Excel, Office e ferramentas de análise para organizar informações e apoiar relatórios.',
          ],
          location: 'São Luís, Maranhão, Brasil',
          startDate: 'Jul/2021',
          endDate: 'Jan/2023',
          current: false,
          image: 'logoSema.jpg',
        },
        {
          id: '3',
          title: 'Bolsista',
          company: 'Laboratório de Ciência e Planejamento Ambiental - LACPLAM',
          description: [
            'Análise de uso e ocupação da sub-bacia do rio Bacanga, em São Luís - MA.',
            'Processamento de dados com geoprocessamento, QGIS e imagens de satélite.',
          ],
          location: 'São Luís, Maranhão, Brasil · No local',
          startDate: 'Ago/2021',
          endDate: 'Ago/2022',
          current: false,
          image: 'logo-ufma.png',
        },
        {
          id: '2',
          title: 'Estagiário',
          company: 'Laboratório de Estudos e Modelagem Climática - LACLIMA',
          description: [
            'Análise e processamento de dados meteoceanográficos para pesquisas e iniciativas acadêmicas.',
            'Participação em PIBIC e outros projetos científicos com foco em dados climáticos.',
          ],
          location: 'São Luís, Maranhão, Brasil · No local',
          startDate: 'Ago/2018',
          endDate: 'Mar/2020',
          current: false,
          image: 'logo-ufma.png',
        },
        {
          id: '1',
          title: 'Bolsista',
          company: 'Laboratório de Estudos e Modelagem Climática - LACLIMA',
          description: [
            'Apoio em pesquisas, aquisição de conhecimento técnico e colaboração em equipe.',
            'Atuação com análise de dados climáticos e organização de informações para estudos acadêmicos.',
          ],
          location: 'São Luís, Maranhão, Brasil · No local',
          startDate: 'Ago/2018',
          endDate: 'Mar/2020',
          current: false,
          image: 'logo-ufma.png',
        },
      ],
    },
    contact: {
      title: 'Contato',
      description:
        'Quer conversar sobre uma API, e-commerce, dashboard ou produto digital? Me envie uma mensagem direta por email ou use o formulário abaixo.',
      downloadCv: 'Baixar CV',
      ctaLabel: 'Fale comigo',
      ctaDescription: 'Disponível para conversar sobre oportunidades, freelas e parcerias.',
      emailSubject: 'Contato pelo portfólio',
      nameLabel: 'Seu nome',
      namePlaceholder: 'Como posso chamá-lo?',
      emailLabel: 'Seu email',
      emailPlaceholder: 'Qual seu email?',
      messageLabel: 'Sua mensagem',
      messagePlaceholder: 'No que está pensando?',
      submit: 'Enviar',
      sending: 'Enviando...',
      required: 'Por favor, preencha todos os campos.',
      success: 'Email enviado com sucesso!',
      error: 'Erro ao enviar email. Tente novamente mais tarde.',
      formNotConfigured:
        'Formulário de contato não configurado. Defina REACT_APP_WEB3FORMS_ACCESS_KEY no ambiente de build.',
    },
  },
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      projects: 'Projects',
      education: 'Education',
      resume: 'Experience',
      contact: 'Contact',
      blog: 'Blog',
      language: 'Language',
    },
    home: {
      eyebrow: 'Backend Developer',
      title: 'I build scalable backend solutions for modern digital products.',
      description:
        'Hi, I am Átila Rocha, a developer specialized in Node.js, NestJS, TypeScript, and API architecture. I help companies turn ideas into robust, secure systems ready to grow.',
      primaryAction: 'Contact me',
      secondaryAction: 'View experience',
      backToTop: 'Back to top',
    },
    about: {
      title: 'About the Developer',
      readMore: 'Read More',
      readLess: 'Show Less',
      imageAltOne: 'Átila Rocha in a professional moment',
      imageAltTwo: 'Átila Rocha professional profile',
      paragraphs: [
        'Hi, my name is Átila, and my path in technology is shaped by curiosity, determination, and a constant desire to learn. It started during my Oceanography degree, when I first used programming to analyze complex data with Matlab and Linux.',
        'I completed my Oceanography degree and I am currently studying Systems Analysis and Development. My motivation comes from solving real problems with efficiency, technical clarity, and solutions that can evolve with the product.',
        'Today I work mainly with backend development, APIs, databases, dashboards, and web systems. I enjoy turning business rules into stable, well-structured, maintainable applications.',
        'I am self-taught, constantly learning, and focused on applying good practices in architecture, version control, containerization, and team collaboration.',
        'One delivery I am especially proud of was a geolocation application for identifying bioactives at UFMA, connecting my Oceanography background with software development.',
        'I value empathy, teamwork, and technical excellence. I believe good solutions come from understanding people, business context, and technology together.',
      ],
    },
    projects: {
      title: 'Projects',
      description: 'Main applications completed and in development.',
      github: 'GitHub',
      deploy: 'Deploy',
      inProgressAlt: 'Project in progress',
      items: [
        {
          name: 'Voting System',
          summary:
            'Web system for managing and running online voting processes, using modern technologies with a focus on security and efficiency.',
          technologies: ['PHP', 'Laravel', 'Blade', 'JavaScript'],
          githubLink: 'https://github.com/AtilaRocha/voting-system',
          liveLink: '',
          image: 'projeto1.png',
          status: 'finished',
        },
        {
          name: 'Portfolio',
          summary: 'Project created to present my career, experience, projects, and programming journey.',
          technologies: ['React', 'JavaScript', 'Typescript', 'HTML', 'CSS'],
          githubLink: '',
          liveLink: 'https://atilarocha.netlify.app/',
          image: 'projeto3.png',
          status: 'finished',
        },
        {
          name: 'Careers Form',
          summary:
            'Web application for managing job application forms, with PHP/Laravel backend, HTML, CSS, and JavaScript frontend, MySQL database, and Docker environment.',
          technologies: ['PHP', 'Laravel', 'Docker', 'MySQL', 'JavaScript'],
          githubLink: 'https://github.com/AtilaRocha/mxmz-tech',
          liveLink: '',
          image: 'projeto4.png',
          status: 'finished',
        },
        {
          name: 'HydroDataViewer',
          summary:
            'Django application that consumes Brazil National Water Agency data to display hydrometeorological information such as water levels, rainfall, and temperature.',
          technologies: ['Python', 'Django'],
          githubLink: 'https://github.com/AtilaRocha/HydroDataViewer',
          liveLink: '',
          image: 'projeto2.png',
          status: 'unfinished',
        },
      ],
    },
    education: {
      title: 'Education',
      description: 'Academic background summary.',
      items: [
        {
          title: 'Systems Analysis and Development',
          institution: 'Ampli',
          location: '',
          startDate: 'Mar/2023',
          endDate: 'Present',
          description:
            'Skills: requirements gathering, software development, object-oriented programming, Java, Python, and MySQL.',
        },
        {
          title: 'Oceanography',
          institution: 'UFMA - Federal University of Maranhão',
          location: 'São Luís',
          startDate: 'Mar/2018',
          endDate: 'Jul/2024',
          description:
            'Scientific background in environmental data analysis, communication, geography, applied research, and programming.',
        },
      ],
    },
    resume: {
      title: 'Professional Experience',
      description: 'Work history and previous experience.',
      items: [] as ResumeItem[],
    },
    contact: {
      title: 'Contact',
      description:
        'Want to talk about an API, e-commerce, dashboard, or digital product? Send me a direct email or use the form below.',
      downloadCv: 'Download CV',
      ctaLabel: 'Contact me',
      ctaDescription: 'Available to talk about opportunities, freelance work, and partnerships.',
      emailSubject: 'Contact from portfolio',
      nameLabel: 'Your name',
      namePlaceholder: 'How should I call you?',
      emailLabel: 'Your email',
      emailPlaceholder: 'What is your email?',
      messageLabel: 'Your message',
      messagePlaceholder: 'What are you thinking about?',
      submit: 'Send',
      sending: 'Sending...',
      required: 'Please fill in all fields.',
      success: 'Email sent successfully!',
      error: 'Error sending email. Please try again later.',
      formNotConfigured:
        'Contact form is not configured. Set REACT_APP_WEB3FORMS_ACCESS_KEY in the build environment.',
    },
  },
  es: {
    nav: {
      home: 'Inicio',
      about: 'Sobre mí',
      projects: 'Proyectos',
      education: 'Educación',
      resume: 'Experiencia',
      contact: 'Contacto',
      blog: 'Blog',
      language: 'Idioma',
    },
    home: {
      eyebrow: 'Backend Developer',
      title: 'Construyo soluciones backend escalables para productos digitales modernos.',
      description:
        'Hola, soy Átila Rocha, desarrollador especializado en Node.js, NestJS, TypeScript y arquitectura de APIs. Ayudo a empresas a convertir ideas en sistemas robustos, seguros y preparados para crecer.',
      primaryAction: 'Hablemos',
      secondaryAction: 'Ver experiencia',
      backToTop: 'Volver arriba',
    },
    about: {
      title: 'Sobre el Desarrollador',
      readMore: 'Leer Más',
      readLess: 'Mostrar Menos',
      imageAltOne: 'Átila Rocha en un momento profesional',
      imageAltTwo: 'Perfil profesional de Átila Rocha',
      paragraphs: [
        'Hola, mi nombre es Átila, y mi camino en tecnología está marcado por curiosidad, determinación y ganas constantes de aprender. Todo comenzó durante mi carrera de Oceanografía, cuando usé programación para analizar datos complejos con Matlab y Linux.',
        'Terminé Oceanografía y actualmente estudio Análisis y Desarrollo de Sistemas. Mi motivación viene de resolver problemas reales con eficiencia, claridad técnica y soluciones capaces de evolucionar con el producto.',
        'Hoy trabajo principalmente con backend, APIs, bases de datos, dashboards y sistemas web. Me gusta transformar reglas de negocio en aplicaciones estables, bien estructuradas y fáciles de mantener.',
        'Soy autodidacta, estudio continuamente y busco aplicar buenas prácticas en arquitectura, control de versiones, contenedores y colaboración en equipo.',
        'Una entrega de la que me siento especialmente orgulloso fue una aplicación de geolocalización para identificar bioactivos en la UFMA, conectando mi base en Oceanografía con el desarrollo de software.',
        'Valoro la empatía, el trabajo en equipo y la excelencia técnica. Creo que las buenas soluciones nacen al entender a las personas, el negocio y la tecnología.',
      ],
    },
    projects: {
      title: 'Proyectos',
      description: 'Principales aplicaciones terminadas y en desarrollo.',
      github: 'GitHub',
      deploy: 'Deploy',
      inProgressAlt: 'Proyecto en desarrollo',
      items: [
        {
          name: 'Sistema de Votación',
          summary:
            'Sistema web para gestionar y realizar votaciones online, usando tecnologías modernas con foco en seguridad y eficiencia.',
          technologies: ['PHP', 'Laravel', 'Blade', 'JavaScript'],
          githubLink: 'https://github.com/AtilaRocha/voting-system',
          liveLink: '',
          image: 'projeto1.png',
          status: 'finished',
        },
        {
          name: 'Portafolio',
          summary: 'Proyecto creado para presentar mi carrera, experiencias, proyectos y trayectoria en programación.',
          technologies: ['React', 'JavaScript', 'Typescript', 'HTML', 'CSS'],
          githubLink: '',
          liveLink: 'https://atilarocha.netlify.app/',
          image: 'projeto3.png',
          status: 'finished',
        },
        {
          name: 'Formulario - Trabaja con Nosotros',
          summary:
            'Aplicación web para gestionar formularios de candidatura, con backend PHP/Laravel, frontend HTML, CSS y JavaScript, base MySQL y entorno Docker.',
          technologies: ['PHP', 'Laravel', 'Docker', 'MySQL', 'JavaScript'],
          githubLink: 'https://github.com/AtilaRocha/mxmz-tech',
          liveLink: '',
          image: 'projeto4.png',
          status: 'finished',
        },
        {
          name: 'HydroDataViewer',
          summary:
            'Aplicación en Django que consume datos de la Agencia Nacional de Aguas de Brasil para mostrar información hidrometeorológica como niveles de agua, lluvia y temperatura.',
          technologies: ['Python', 'Django'],
          githubLink: 'https://github.com/AtilaRocha/HydroDataViewer',
          liveLink: '',
          image: 'projeto2.png',
          status: 'unfinished',
        },
      ],
    },
    education: {
      title: 'Educación',
      description: 'Resumen de formación académica.',
      items: [
        {
          title: 'Análisis y Desarrollo de Sistemas',
          institution: 'Ampli',
          location: '',
          startDate: 'Mar/2023',
          endDate: 'Actual',
          description:
            'Competencias: levantamiento de requisitos, desarrollo de software, programación orientada a objetos, Java, Python y MySQL.',
        },
        {
          title: 'Oceanografía',
          institution: 'UFMA - Universidad Federal de Maranhão',
          location: 'São Luís',
          startDate: 'Mar/2018',
          endDate: 'Jul/2024',
          description:
            'Base científica en análisis de datos ambientales, comunicación, geografía, investigación aplicada y programación.',
        },
      ],
    },
    resume: {
      title: 'Experiencia Profesional',
      description: 'Historial laboral y experiencias anteriores.',
      items: [] as ResumeItem[],
    },
    contact: {
      title: 'Contacto',
      description:
        '¿Quieres hablar sobre una API, e-commerce, dashboard o producto digital? Envíame un email directo o usa el formulario abajo.',
      downloadCv: 'Descargar CV',
      ctaLabel: 'Hablemos',
      ctaDescription: 'Disponible para hablar sobre oportunidades, freelas y colaboraciones.',
      emailSubject: 'Contacto desde el portafolio',
      nameLabel: 'Tu nombre',
      namePlaceholder: '¿Cómo puedo llamarte?',
      emailLabel: 'Tu email',
      emailPlaceholder: '¿Cuál es tu email?',
      messageLabel: 'Tu mensaje',
      messagePlaceholder: '¿Qué tienes en mente?',
      submit: 'Enviar',
      sending: 'Enviando...',
      required: 'Por favor, completa todos los campos.',
      success: 'Email enviado correctamente.',
      error: 'Error al enviar el email. Inténtalo de nuevo más tarde.',
      formNotConfigured:
        'Formulario de contacto no configurado. Define REACT_APP_WEB3FORMS_ACCESS_KEY en el entorno de build.',
    },
  },
};

const englishResumeDescriptions: Record<string, string[]> = {
  '10': [
    'Backend work on e-commerce solutions, integrating APIs, microservices, and critical flows such as orders, payments, and inventory.',
    'Development with Node.js, NestJS, and TypeScript, applying clean architecture, maintainable code, and good practices for scalable systems.',
    'Deployment and support with Docker, CI/CD, Git, Bitbucket, Jenkins, and AWS/Oracle cloud while collaborating with a distributed LATAM team.',
  ],
  '9': [
    'Development of web applications with Node.js, Next.js, Java, Spring Boot, and relational databases.',
    'Implementation of projects for different clients, including WebGIS, interactive dashboards, and responsive interfaces.',
    'End-to-end delivery, from technical discovery to deployment and feature support.',
  ],
  '8': [
    'Development and maintenance of applications, creating robust solutions for client needs.',
    'Implementation of dynamic and responsive interfaces for a fluid user experience.',
    'Configuration of containerized environments, database integrations, and support for scalable solutions.',
  ],
  '7': [
    'Initial application setup, creating a solid foundation for team collaboration.',
    'Planning and prototyping UI/UX components to improve user experience.',
    'Creation of systems for image analysis, geospatial data processing, and sensitive resources with access control.',
  ],
  '6': [
    'Development of APIs and interactive systems for demands across different areas.',
    'Participation in responsive and intuitive interface creation, improving usability and overall experience.',
    'Management of development and production environments focused on availability, reliability, and scalability.',
  ],
  '5': [
    'Environmental analysis and monitoring for the State of Maranhão.',
    'Creation of environmental reports with Python and Excel for data analysis, automation, and presentation.',
  ],
  '4': [
    'Support for administrative and technical routines related to environmental data.',
    'Use of Excel, Office, and analysis tools to organize information and support reports.',
  ],
  '3': [
    'Analysis of land use and occupation in the Bacanga river sub-basin in São Luís, MA.',
    'Data processing with geoprocessing, QGIS, and satellite imagery.',
  ],
  '2': [
    'Analysis and processing of meteoceanographic data for research and academic initiatives.',
    'Participation in PIBIC and other scientific projects focused on climate data.',
  ],
  '1': [
    'Support for research, technical learning, and team collaboration.',
    'Work with climate data analysis and information organization for academic studies.',
  ],
};

const spanishResumeDescriptions: Record<string, string[]> = {
  '10': [
    'Trabajo backend en soluciones de e-commerce, integrando APIs, microservicios y flujos críticos como pedidos, pagos e inventario.',
    'Desarrollo con Node.js, NestJS y TypeScript, aplicando arquitectura limpia, código mantenible y buenas prácticas para sistemas escalables.',
    'Deploy y soporte con Docker, CI/CD, Git, Bitbucket, Jenkins y cloud AWS/Oracle en colaboración con un equipo distribuido en LATAM.',
  ],
  '9': [
    'Desarrollo de aplicaciones web con Node.js, Next.js, Java, Spring Boot y bases de datos relacionales.',
    'Implementación de proyectos para clientes diversos, incluyendo WebGIS, dashboards interactivos e interfaces responsivas.',
    'Entrega completa, desde el levantamiento técnico hasta el deploy y soporte de funcionalidades.',
  ],
  '8': [
    'Desarrollo y mantenimiento de aplicaciones, creando soluciones robustas para necesidades de clientes.',
    'Implementación de interfaces dinámicas y responsivas para una experiencia de usuario fluida.',
    'Configuración de ambientes con contenedores, integraciones con bases de datos y apoyo en soluciones escalables.',
  ],
  '7': [
    'Desarrollo del setup inicial de aplicaciones, creando bases sólidas para la colaboración en equipo.',
    'Planificación y prototipado de componentes UI/UX para optimizar la experiencia del usuario.',
    'Creación de sistemas para análisis de imágenes, procesamiento de datos geoespaciales y recursos sensibles con control de acceso.',
  ],
  '6': [
    'Desarrollo de APIs y sistemas interactivos para atender demandas de diferentes áreas.',
    'Participación en la creación de interfaces responsivas e intuitivas, mejorando usabilidad y experiencia general.',
    'Gestión de ambientes de desarrollo y producción con foco en disponibilidad, confiabilidad y escalabilidad.',
  ],
  '5': [
    'Análisis y monitoreo ambiental del Estado de Maranhão.',
    'Elaboración de informes ambientales con Python y Excel para análisis, automatización y presentación de datos.',
  ],
  '4': [
    'Apoyo en rutinas administrativas y técnicas relacionadas con datos ambientales.',
    'Uso de Excel, Office y herramientas de análisis para organizar información y apoyar informes.',
  ],
  '3': [
    'Análisis de uso y ocupación de la subcuenca del río Bacanga, en São Luís - MA.',
    'Procesamiento de datos con geoprocesamiento, QGIS e imágenes satelitales.',
  ],
  '2': [
    'Análisis y procesamiento de datos meteoceanográficos para investigaciones e iniciativas académicas.',
    'Participación en PIBIC y otros proyectos científicos con foco en datos climáticos.',
  ],
  '1': [
    'Apoyo en investigaciones, aprendizaje técnico y colaboración en equipo.',
    'Trabajo con análisis de datos climáticos y organización de información para estudios académicos.',
  ],
};

translations.en.resume.items = translations.pt.resume.items.map((item) => ({
  ...item,
  title:
    item.company === 'Freelance | Self-Employed'
      ? 'Freelance Developer'
      : item.title === 'Desenvolvedor de Software'
        ? 'Software Developer'
        : item.title === 'Analista de Dados'
          ? 'Data Analyst'
          : item.title === 'Estagiário'
            ? 'Intern'
            : item.title === 'Bolsista'
              ? 'Research Fellow'
              : item.title,
  location: item.location
    .replace('Remoto', 'Remote')
    .replace('São Luís, Maranhão, Brasil', 'São Luís, Maranhão, Brazil')
    .replace('Presencial', 'On-site')
    .replace('Híbrido', 'Hybrid')
    .replace('No local', 'On-site'),
  endDate: item.endDate === 'Atual' ? 'Present' : item.endDate,
  description: englishResumeDescriptions[item.id] ?? item.description,
}));

translations.es.resume.items = translations.pt.resume.items.map((item) => ({
  ...item,
  title:
    item.company === 'Freelance | Self-Employed'
      ? 'Desarrollador Freelancer'
      : item.title === 'Desenvolvedor de Software'
        ? 'Desarrollador de Software'
        : item.title === 'Analista de Dados'
          ? 'Analista de Datos'
          : item.title === 'Estagiário'
            ? 'Pasante'
            : item.title === 'Bolsista'
              ? 'Becario'
              : item.title,
  location: item.location
    .replace('Remoto', 'Remoto')
    .replace('São Luís, Maranhão, Brasil', 'São Luís, Maranhão, Brasil')
    .replace('Presencial', 'Presencial')
    .replace('Híbrido', 'Híbrido')
    .replace('No local', 'Presencial'),
  endDate: item.endDate === 'Atual' ? 'Actual' : item.endDate,
  description: spanishResumeDescriptions[item.id] ?? item.description,
}));
